/**
 * LiveCounter — sticky social-proof widget for the marketing pages.
 *
 * Pulls /live-stats every 30s. Three rotating metrics so it always feels
 * "alive" without being noisy. Animated number-roll on mount.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { TrendingUp } from "lucide-react";
import { API } from "../config";

export function LiveCounter({ language = "en" }) {
  const [stats, setStats] = useState(null);
  const [displayed, setDisplayed] = useState(0);
  const [idx, setIdx] = useState(0);
  const rotateRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/live-stats`);
        if (!cancelled) setStats(r.data);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const sk = ["sk", "cs"].includes(language);
  const labels = sk
    ? [
        { key: "combined_week",     text: "návštev z Google + AI tento týždeň" },
        { key: "ai_citations_week", text: "citácií v ChatGPT a Perplexity tento týždeň" },
        { key: "scans_total",       text: "auditov dokončených od spustenia" },
      ]
    : [
        { key: "combined_week",     text: "visits from Google + AI this week" },
        { key: "ai_citations_week", text: "citations in ChatGPT and Perplexity this week" },
        { key: "scans_total",       text: "audits completed since launch" },
      ];

  useEffect(() => {
    if (!stats) return;
    rotateRef.current = setInterval(() => setIdx(i => (i + 1) % labels.length), 4000);
    return () => clearInterval(rotateRef.current);
  }, [stats, labels.length]);

  useEffect(() => {
    if (!stats) return;
    const target = stats[labels[idx].key] || 0;
    const start = displayed;
    const duration = 600;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setDisplayed(Math.round(start + (target - start) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, stats]);

  if (!stats) return null;

  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2 bg-[var(--color-surface)] border border-[#10B981]/30 rounded-full"
      data-testid="live-counter"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#10B981]" />
      </span>
      <TrendingUp className="w-4 h-4 text-[#10B981]" />
      <span className="text-sm">
        <span className="font-black text-white font-mono" data-testid="live-counter-value">
          {displayed.toLocaleString()}
        </span>{" "}
        <span className="text-[var(--color-text-muted)]">{labels[idx].text}</span>
      </span>
    </div>
  );
}

export default LiveCounter;
